import { Button, Circle, styled, type TextProps, View, XStack, type XStackProps, YStack, type YStackProps } from "tamagui";
import { backgroundPrimaryHex, strokeSecondaryHex, textPrimaryHex } from "../themes/legacy/colors-granted";
import { hairline } from "../utils";
export const StyledCircle = styled(Circle, {
  size: "$5",
  backgroundColor: "$groupedSecondary",
  zIndex: 2,
  justifyContent: "center",
  alignItems: "center",
  mb: "$3.5",
  variants: {
    whiteShadow: {
      true: {
        backgroundColor: "$background",
        shadowColor: "black",
        shadowOffset: {
          height: 4,
          width: 0
        },
        shadowRadius: 4,
        shadowOpacity: 0.08
      },
      false: {}
    }
  } as const
});
export const SquareIconWrapper = styled(XStack, {
  height: "$3",
  width: "$3",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "$3",
  backgroundColor: "$backgroundTertiary"
});
export const AnimatedYStackAnimationProps: YStackProps = {
  exitStyle: {
    transform: [{
      translateY: 50
    }],
    scale: 0,
    opacity: 0
  },
  enterStyle: {
    transform: [{
      translateY: 50
    }],
    scale: 0.5,
    opacity: 0
  },
  transition: "200ms easeCubic"
};
export const AnimatedYStack = styled(YStack, {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  transform: [{
    translateY: 0
  }],
  justifyContent: "flex-end",
  alignItems: "center"
});
export const AnimatedStackIconWrapper = styled(YStack, {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  top: 0,
  justifyContent: "center",
  alignItems: "center",
  transition: "bouncyTight",
  enterStyle: {
    opacity: 0,
    scale: 0.5
  },
  exitStyle: {
    opacity: 0
  }
});
export const AnimatedXStackAnimationProps: XStackProps = {
  enterStyle: {
    opacity: 0,
    scale: 0
  },
  exitStyle: {
    opacity: 0,
    scale: 0
  },
  transition: "150ms easeOut"
};
export const AnimatedXStack = styled(XStack, {
  opacity: 1,
  scale: 1,
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  f: 1,
  jc: "center",
  ai: "center"
});
export const AnimatedTextProps: TextProps = {
  position: "absolute",
  top: 0,
  right: 0,
  left: 0,
  f: 1,
  textAlign: "center",
  transition: "200ms easeCubic",
  enterStyle: {
    opacity: 0
  },
  exitStyle: {
    opacity: 0
  }
};
export const RefreshButtonClickProps: XStackProps = {
  shadowOffset: {
    width: 0,
    height: 2
  },
  shadowOpacity: 0.08,
  shadowRadius: 4,
  shadowColor: "black",
  pressStyle: {
    scale: 0.95
  },
  transition: "bouncyTight"
};
export const StyledRefreshButton = styled(XStack, {
  cursor: "pointer",
  gap: "$2",
  px: "$3",
  py: "$2",
  borderWidth: hairline,
  borderColor: "$strokeSecondary",
  backgroundColor: "$background",
  borderRadius: "$10",
  justifyContent: "center",
  alignItems: "center",
  shadowOffset: {
    width: 0,
    height: 2
  },
  shadowOpacity: 0.08,
  shadowRadius: 4,
  shadowColor: "black",
  pressStyle: {
    scale: 0.95
  },
  transition: "bouncyTight"
});
export const StyledLinkStatus = styled(XStack, {
  justifyContent: "space-between",
  alignItems: "center",
  gap: "$2",
  px: "$3",
  py: "$2",
  borderRadius: "$10",
  borderWidth: hairline,
  borderColor: "$color4",
  backgroundColor: "$color3"
});
export const StyledMultiActionButton = styled(Button, {
  name: "StyledMultiActionButton",
  unstyled: true,
  cursor: "pointer",
  borderRadius: "$10",
  height: "$6",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  paddingRight: "$4",
  paddingLeft: "$3",
  backgroundColor: backgroundPrimaryHex,
  borderWidth: 1,
  borderColor: strokeSecondaryHex,
  transition: "bouncyTight",
  hoverStyle: {
    backgroundColor: "white",
    borderColor: "$cream10",
    shadowColor: backgroundPrimaryHex,
    shadowOpacity: 0.8,
    shadowRadius: 10
  },
  pressStyle: {
    backgroundColor: "white",
    borderColor: "$cream10",
    shadowColor: backgroundPrimaryHex,
    shadowOpacity: 0.8,
    shadowRadius: 14,
    scale: 0.98
  },
  variants: {
    selected: {
      true: {
        borderColor: textPrimaryHex,
        shadowOffset: {
          width: 0,
          height: 4
        },
        shadowColor: "black",
        shadowOpacity: 0.08,
        shadowRadius: 8
      }
    }
  } as const
});
export const StyledNextActionButtonProps: XStackProps = {
  cursor: "pointer",
  br: "$10",
  height: "$6",
  width: "$7",
  transition: "bouncyTight",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: textPrimaryHex,
  hoverStyle: {
    backgroundColor: "#0a2d70"
  },
  pressStyle: {
    backgroundColor: "#143980",
    scale: 0.98
  }
};
const StyledNextActionButtonInner = styled(Button, {
  name: "StyledNextActionButton",
  unstyled: true,
  cursor: "pointer",
  br: "$10",
  height: "$6",
  width: "$7",
  transition: "bouncyTight",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: textPrimaryHex,
  hoverStyle: {
    backgroundColor: "#0a2d70"
  },
  pressStyle: {
    backgroundColor: "#143980",
    scale: 0.98
  }
});
export const StyledNextActionButton = (props: React.ComponentProps<typeof StyledNextActionButtonInner>) => <Button.Apply color={backgroundPrimaryHex} data-sentry-element="Button.Apply" data-sentry-component="StyledNextActionButton" data-sentry-source-file="GeneralStyledComponents.tsx">
    <StyledNextActionButtonInner {...props} data-sentry-element="StyledNextActionButtonInner" data-sentry-source-file="GeneralStyledComponents.tsx" />
  </Button.Apply>;
export const FeedbackIconWrapper = styled(View, {
  position: "absolute",
  left: 8,
  top: 8,
  width: 16,
  height: 16,
  zIndex: 1
});
export const FeedbackButton = styled(XStack, {
  render: "button",
  unstyled: true,
  width: "100%",
  height: "100%",
  cursor: "pointer",
  backgroundColor: "transparent",
  position: "relative",
  overflow: "hidden"
});

// Normally we'd use $background, but in Granted’s theme (see
// component-themes.ts) for buttons,
// this maps buttons to dark blue. To avoid unintended global changes, we use $white1.
const StyledSelectButtonInner = styled(Button, {
  unstyled: true,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  margin: 0,
  borderWidth: 1,
  borderColor: "$strokePrimary",
  backgroundColor: "$white1",
  borderRadius: "$10",
  height: 50,
  paddingHorizontal: "$4",
  animateOnly: ["backgroundColor", "borderColor", "opacity"],
  hoverStyle: {
    borderColor: "$textSecondary",
    backgroundColor: "$white1"
  },
  pressStyle: {
    margin: 0,
    borderWidth: 2,
    borderColor: "$borderColorPress",
    backgroundColor: "$white1",
    scale: 0.98
  },
  variants: {
    selected: {
      true: {
        margin: 0,
        borderWidth: 2,
        borderColor: "$borderColorFocus",
        backgroundColor: "$white1"
      }
    }
  } as const
});
export const StyledSelectButton = ({
  children,
  ...props
}: React.ComponentProps<typeof StyledSelectButtonInner>) => <StyledSelectButtonInner {...props} data-sentry-element="StyledSelectButtonInner" data-sentry-component="StyledSelectButton" data-sentry-source-file="GeneralStyledComponents.tsx">
    {/* eslint-disable-next-line react/forbid-component-props */}
    <Button.Text color="$color13" fontWeight="500" size="$4" data-sentry-element="Button.Text" data-sentry-source-file="GeneralStyledComponents.tsx">
      {children}
    </Button.Text>
  </StyledSelectButtonInner>;
export const StyledPlaceholderLine = styled(XStack, {
  height: 8,
  width: "100%",
  backgroundColor: "$gray5",
  borderRadius: 2
});